import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GoogleMap, GoogleMapsModule, MapAdvancedMarker } from '@angular/google-maps';
import { ModalComponent, ModalService, Size } from '@circlek-global/lds-components';

import { GOOGLE_MAP_ID } from '~constants/map.constants';
import type { Station } from '~models/station.model';
import { MapService } from '~services/map-service';

@Component({
  selector: 'app-map-modal',
  imports: [GoogleMap, MapAdvancedMarker, GoogleMapsModule, ModalComponent],
  templateUrl: './map-modal.component.html',
  styles: `
    :host ::ng-deep lds-modal dialog > lds-button {
      z-index: 10;
    }
  `,
  providers: [ModalService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapModalComponent {
  private readonly mapService = inject(MapService);
  private readonly modalService = inject(ModalService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  readonly station = input.required<Station>();
  readonly close = output();
  protected readonly Size = Size;
  protected readonly GOOGLE_MAP_ID = GOOGLE_MAP_ID;
  protected readonly modal = viewChild.required<ModalComponent>('modal');
  protected readonly mapElement = viewChild.required<GoogleMap>('modalMap');
  protected readonly mapOptions = computed(() => ({
    ...this.mapService.mapConfig(),
    center: {
      lat: this.station().latitude,
      lng: this.station().longitude,
    },
    zoom: 15,
    fullscreenControl: false,
  }));
  protected readonly marker = computed(() => {
    const station = this.station();
    const marker = this.mapService.getCustomMarkers([station], station.id)[0];

    return marker;
  });

  ngAfterViewInit(): void {
    this.modalService
      .open(this.modal())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.close.emit();
      });

    this.cdr.detectChanges();
  }

  onMapInitialized(): void {
    this.mapService.onMapInitialized(this.mapElement());
  }
}
